import { Injectable } from "@angular/core";
import { DataService } from "./data.service";
import {
  Patient,
  PatientImage,
  PatientSearchResult,
  PhiPatientApiRecord,
  PhiPatientApiResponse,
  UpsertPatient,
  PatientContact,
  PatientBiometricsPatch,
  coalescePhiPatientPostResponse,
  parsePhiPatientApiResponse,
  PATIENT_PHARMACY_FIELD_KEYS,
} from "../models/patient.model";
import {
  catchError,
  finalize,
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
  tap,
  throwError,
} from "rxjs";
import { environment } from "src/environments/environment";
import { ApiStatus, ReferralApis, Utils } from "./utils";
import { ApplicationCacheService } from "./application-cache.service";
import { normalizeEngageReferralSourceIdField } from "./engage-patient.service";
import { cloneDeep } from "lodash";
import { Appointment } from "../models/admissions.model";
import * as crypto from "crypto-js";
import { formatEncounterPatientDisplayFields } from "../helpers/phi-encounter-patient.util";

/** Options for PHI `POST/PATCH api/v1/patients` (e.g. worklist skips GET-by-id hydrate). */
export interface PhiPatientWriteOptions {
  skipGetByIdHydrate?: boolean;
}

@Injectable({
  providedIn: "root",
})
export class PatientService {
  /** Base64 prefix for OpenSSL/CryptoJS `Salted__` ciphertext (PHI fields from some APIs). */
  private static readonly PHI_OPENSSL_PREFIX = "U2FsdGVkX1";
  /** Coalesces concurrent `GET …/encounters/patient/{id}` for the same patient. */
  private readonly patientEncountersInflight = new Map<string, Observable<Appointment[]>>();

  /** Patient id that `applicationCache.patientEncounters` belongs to; null when cache is empty or cleared. */
  private patientEncountersCachePatientId: string | null = null;

  /** Resolve the patient id for an encounters list; null when empty or rows disagree. */
  static resolveEncountersOwnerPatientId(
    encounters: Appointment[] | null | undefined
  ): string | null {
    if (!encounters?.length) {
      return null;
    }
    let ownerId: string | null = null;
    for (const row of encounters) {
      const rowPid = (row?.patientId || row?.encountersPatient?.id || "").trim();
      if (!rowPid) {
        continue;
      }
      if (!ownerId) {
        ownerId = rowPid;
      } else if (ownerId !== rowPid) {
        return null;
      }
    }
    return ownerId;
  }

  static encountersBelongToPatient(
    encounters: Appointment[] | null | undefined,
    patientId: string
  ): boolean {
    const pid = (patientId || "").trim();
    if (!pid) {
      return false;
    }
    const ownerId = PatientService.resolveEncountersOwnerPatientId(encounters);
    return !!ownerId && ownerId === pid;
  }

  /** Drop cached encounters when they belong to a different patient (or are ambiguous). */
  clearPatientEncountersCacheUnlessPatient(patientId: string): void {
    const pid = (patientId || "").trim();
    const cached = this._applicationCacheService.applicationCache.patientEncounters || [];
    if (!cached.length) {
      this.patientEncountersCachePatientId = null;
      return;
    }
    if (!pid || !PatientService.encountersBelongToPatient(cached, pid)) {
      this._applicationCacheService.applicationCache.patientEncounters = [];
      this.patientEncountersCachePatientId = null;
    }
  }

  private setPatientEncountersCache(patientId: string, encounters: Appointment[]): void {
    const pid = (patientId || "").trim();
    this._applicationCacheService.applicationCache.patientEncounters = encounters;
    this.patientEncountersCachePatientId = pid || null;
  }

  /** Stringify location-ish API values; supports nested `{ name, displayName }` (avoids "[object Object]"). */
  private static coerceLocationString(val: unknown): string {
    if (val == null) return "";
    if (typeof val === "string") return val.trim();
    if (typeof val === "number" || typeof val === "boolean") return String(val).trim();
    if (typeof val === "object") {
      const o = val as Record<string, unknown>;
      const n =
        o.name ?? o.displayName ?? o.label ?? o.locationName ?? o.LocationName ?? o.title;
      if (typeof n === "string" && n.trim()) return n.trim();
    }
    return "";
  }

  /**
   * Trim API string fields; treats missing values safely (never yields the literal `"undefined"` from `String(undefined)`).
   */
  private static coerceTextField(val: unknown): string {
    if (val == null) {
      return "";
    }
    const t = typeof val === "string" ? val.trim() : String(val).trim();
    if (!t || t === "undefined" || t === "null") {
      return "";
    }
    return t;
  }

  /** `procedureRoom` / `Schedule` block may be camelCase, snake_case, or PascalCase (e.g. .NET JSON). */
  private static pickProcedureRoomFromRecord(obj: Record<string, unknown> | undefined): string {
    if (!obj) {
      return "";
    }
    const keys = [
      "procedureRoom",
      "procedure_room",
      "ProcedureRoom",
      "Procedure_Room",
    ];
    for (const k of keys) {
      const v = PatientService.coerceTextField(obj[k]);
      if (v) {
        return v;
      }
    }
    return "";
  }

  private static pickScheduleStatusFromRecord(obj: Record<string, unknown> | undefined): string {
    if (!obj) {
      return "";
    }
    const keys = [
      "scheduleStatus",
      "schedule_status",
      "ScheduleStatus",
      "Schedule_Status",
    ];
    for (const k of keys) {
      const v = PatientService.coerceTextField(obj[k]);
      if (v) {
        return v;
      }
    }
    return "";
  }

  /**
   * Copy known schedule fields from API aliases onto canonical camelCase keys so Engage / modals
   * can read `procedureRoom` and `scheduleStatus` consistently.
   */
  private static mergeScheduleAliasesOntoObject(sched: Record<string, unknown>): void {
    const room = PatientService.pickProcedureRoomFromRecord(sched);
    const st = PatientService.pickScheduleStatusFromRecord(sched);
    if (room) {
      sched["procedureRoom"] = room;
    }
    if (st) {
      sched["scheduleStatus"] = st;
    }
  }

  /**
   * Patient-encounters API often returns `encounterSchedule` as an array of rows; the UI model
   * expects a single {@link EncounterSchedule}. Pick the row that best matches this visit (DOS date)
   * or the latest by `scheduleDate`.
   */
  private static pickPrimaryScheduleRowFromArray(
    encounter: Appointment & Record<string, unknown>,
    rows: unknown[]
  ): Record<string, unknown> | null {
    const objects = rows.filter(
      (r): r is Record<string, unknown> =>
        r != null && typeof r === "object" && !Array.isArray(r)
    );
    if (!objects.length) {
      return null;
    }
    const dosRaw = PatientService.coerceTextField(encounter.encounterDateTime);
    const dosDay =
      dosRaw && dosRaw.length >= 10 && /^\d{4}-\d{2}-\d{2}/.test(dosRaw)
        ? dosRaw.slice(0, 10)
        : "";
    if (dosDay) {
      const match = objects.find((r) => {
        const d =
          PatientService.coerceTextField(r["scheduleDate"]) ||
          PatientService.coerceTextField(r["schedule_date"]);
        return d === dosDay;
      });
      if (match) {
        return match;
      }
    }
    const sorted = [...objects].sort((a, b) => {
      const da =
        PatientService.coerceTextField(a["scheduleDate"]) ||
        PatientService.coerceTextField(a["schedule_date"]) ||
        "";
      const db =
        PatientService.coerceTextField(b["scheduleDate"]) ||
        PatientService.coerceTextField(b["schedule_date"]) ||
        "";
      return db.localeCompare(da);
    });
    return sorted[0];
  }

  /**
   * When `encounterSchedule` / `EncounterSchedule` is an array, replace with one row for downstream code.
   */
  private static flattenEncounterScheduleIfArray(a: Appointment & Record<string, unknown>): void {
    const ax = a as Record<string, unknown>;
    const raw: unknown =
      a.encounterSchedule ?? ax["EncounterSchedule"] ?? ax["encounter_schedule"];

    if (raw == null) {
      return;
    }

    if (Array.isArray(raw)) {
      if (raw.length === 0) {
        a.encounterSchedule = undefined;
        return;
      }
      const picked = PatientService.pickPrimaryScheduleRowFromArray(a, raw);
      if (picked) {
        a.encounterSchedule = picked as unknown as Appointment["encounterSchedule"];
        PatientService.mergeScheduleAliasesOntoObject(picked);
        const st = PatientService.pickScheduleStatusFromRecord(picked);
        if (st && !PatientService.coerceTextField(a.scheduleStatus)) {
          a.scheduleStatus = st;
        }
        const room = PatientService.pickProcedureRoomFromRecord(picked);
        if (room && !PatientService.pickProcedureRoomFromRecord(ax)) {
          ax["procedureRoom"] = room;
        }
      }
      return;
    }

    if (typeof raw === "object" && !Array.isArray(raw)) {
      if (!a.encounterSchedule) {
        a.encounterSchedule = raw as unknown as Appointment["encounterSchedule"];
      }
      PatientService.mergeScheduleAliasesOntoObject(
        a.encounterSchedule as unknown as Record<string, unknown>
      );
    }
  }

  /**
   * Unify `EncounterSchedule` / `schedule` / PascalCase root fields before {@link normalizeEncounterForUi}
   * runs its schedule merge logic.
   */
  private applyPatientEncounterAliases(a: Appointment & Record<string, unknown>): void {
    const ax = a as Record<string, unknown>;

    if (!a.encounterSchedule && ax["EncounterSchedule"] != null) {
      a.encounterSchedule = ax["EncounterSchedule"] as unknown as Appointment["encounterSchedule"];
    }
    if (!a.encounterSchedule && ax["schedule"] != null) {
      a.encounterSchedule = ax["schedule"] as unknown as Appointment["encounterSchedule"];
    }
    if (!a.encounterSchedule && ax["Schedule"] != null) {
      a.encounterSchedule = ax["Schedule"] as unknown as Appointment["encounterSchedule"];
    }

    const rootRoom = PatientService.pickProcedureRoomFromRecord(ax);
    const rootStatus = PatientService.pickScheduleStatusFromRecord(ax);
    if (rootRoom) {
      ax["procedureRoom"] = rootRoom;
    }
    if (rootStatus && !PatientService.coerceTextField(a.scheduleStatus)) {
      a.scheduleStatus = rootStatus;
    }

    const s = a.encounterSchedule;
    if (s != null && typeof s === "object") {
      PatientService.mergeScheduleAliasesOntoObject(
        s as unknown as Record<string, unknown>
      );
    }
  }

  /**
   * Copies anthropometrics from API `schedulePatient` / `schedule_patient` onto {@link Appointment.encounterSchedule}
   * so Engage and headers can read ht/wt without a separate vitals round-trip.
   */
  private mergeSchedulePatientAnthroOntoEncounter(a: Appointment & Record<string, unknown>): void {
    const ax = a as Record<string, unknown>;
    const raw = ax["schedulePatient"] ?? ax["schedule_patient"];
    if (raw == null || typeof raw !== "object" || Array.isArray(raw)) {
      return;
    }
    const sp = raw as Record<string, unknown>;
    const ht =
      PatientService.coerceTextField(sp["ht"]) ||
      PatientService.coerceTextField(sp["height"]) ||
      PatientService.coerceTextField(sp["Height"]);
    const wt =
      PatientService.coerceTextField(sp["wt"]) ||
      PatientService.coerceTextField(sp["weight"]) ||
      PatientService.coerceTextField(sp["Weight"]);
    const measuredOn =
      PatientService.coerceTextField(sp["measuredOn"]) ||
      PatientService.coerceTextField(sp["measured_on"]) ||
      PatientService.coerceTextField(sp["heightMeasuredOn"]) ||
      PatientService.coerceTextField(sp["anthroMeasuredOn"]);
    if (!ht && !wt && !measuredOn) {
      return;
    }
    const prev = a.encounterSchedule;
    const base = (prev && typeof prev === "object" ? { ...(prev as object) } : {}) as Record<
      string,
      unknown
    >;
    if (ht) {
      base["ht"] = ht;
    }
    if (wt) {
      base["wt"] = wt;
    }
    if (measuredOn) {
      base["measuredOn"] = measuredOn;
    }
    a.encounterSchedule = base as unknown as Appointment["encounterSchedule"];
  }

  /**
   * `GET .../encounters/patient/{id}` may embed `encountersPatient` with AES-encrypted strings
   * (same CryptoJS/OpenSSL format as auth storage). Decrypt for UI; leave plain values unchanged.
   */
  private decryptPhiString(value: string): string {
    const s = value.trim();
    if (!s || s.length < 24 || !s.startsWith(PatientService.PHI_OPENSSL_PREFIX)) {
      return value;
    }
    try {
      const bytes = crypto.AES.decrypt(s, environment.signatureKey);
      const plain = bytes.toString(crypto.enc.Utf8);
      if (!plain) {
        return value;
      }
      if (/[\x00-\x08\x0B\x0C\x0E-\x1F]/.test(plain)) {
        return value;
      }
      return plain;
    } catch {
      return value;
    }
  }

  private decryptPhiDeep(value: unknown): unknown {
    if (value == null) {
      return value;
    }
    if (typeof value === "string") {
      return this.decryptPhiString(value);
    }
    if (typeof value !== "object") {
      return value;
    }
    if (value instanceof Date) {
      return value;
    }
    if (Array.isArray(value)) {
      return value.map((x) => this.decryptPhiDeep(x));
    }
    const o = value as Record<string, unknown>;
    const out: Record<string, unknown> = {};
    for (const k of Object.keys(o)) {
      out[k] = this.decryptPhiDeep(o[k]);
    }
    return out;
  }

  private synthesizeEncounterPatientName(ep: Record<string, unknown>): void {
    const existing = String(ep["name"] || "").trim();
    if (existing) {
      return;
    }
    const fn = String(ep["firstName"] || ep["first_name"] || "").trim();
    const ln = String(ep["lastName"] || ep["last_name"] || "").trim();
    const combined = [fn, ln].filter(Boolean).join(" ").trim();
    if (combined) {
      ep["name"] = combined;
    }
  }

  private decryptPhiOnEncounterPatient(a: Appointment & Record<string, unknown>): void {
    const ep = a.encountersPatient;
    if (ep != null && typeof ep === "object") {
      const dec = this.decryptPhiDeep(ep) as Record<string, unknown>;
      formatEncounterPatientDisplayFields(dec);
      a.encountersPatient = dec as Appointment["encountersPatient"];
    }
    const snake = a["encounters_patient"];
    if (snake != null && typeof snake === "object") {
      const decSnake = this.decryptPhiDeep(snake) as Record<string, unknown>;
      formatEncounterPatientDisplayFields(decSnake);
      a["encounters_patient"] = decSnake;
      if (!a.encountersPatient) {
        a.encountersPatient = decSnake as Appointment["encountersPatient"];
      }
    }
  }

  constructor(
    private readonly _dataService: DataService,
    private readonly _applicationCacheService: ApplicationCacheService
  ) {}

  getCompanyPatients(): Observable<Patient[]> {
    const url = this.apiBaseUrl + "api/v1/patients";
    return this._dataService.get(url);
  }

  getPatientById(
    patientId: string,
    options?: { force?: boolean }
  ): Observable<Patient> {
    const pid = (patientId || "").trim();
    const cachedId = (this._applicationCacheService.applicationCache.patient?.id || "").trim();
    if (
      !options?.force &&
      this.isPatientAlreadyLoaded &&
      cachedId &&
      cachedId === pid
    ) {
      return of(this.patientCache);
    }
    const url = this.apiBaseUrl + `api/v1/patients/${patientId}`;
    return this._dataService.get(url).pipe(
      catchError((error) => {
this._applicationCacheService.setApiStatus(
          ApiStatus.Failed,
          ReferralApis.Patient
        );
        return throwError(() => error);
      }),
      tap((patientResponse: Patient) => {
        const patient = {
          ...patientResponse,
          gender: Utils.toTitleCase(patientResponse.gender),
          ethnicity: Utils.toTitleCase(patientResponse.ethnicity),
        };
        const nextId = (patient.id || "").trim();
        const prevId = (this._applicationCacheService.applicationCache.patient?.id || "").trim();
        if (nextId && prevId && nextId !== prevId) {
          this.clearPatientEncountersCacheUnlessPatient(nextId);
        }
        this._applicationCacheService.applicationCache.patient = patient;
        this._applicationCacheService.setApiStatus(
          ApiStatus.Success,
          ReferralApis.Patient
        );
      })
    );
  }

  /**
   * GET encounters for a patient. Normalizes common API shapes (bare array vs `{ data: [...] }`)
   * and fills `encounterSchedule` from root-level fields when missing so headers / Engage show DOS, location, visit type.
   *
   * Concurrent callers for the same patient share one HTTP request. Pass `{ bypassCache: true }`
   * after schedule mutations to force a fresh fetch.
   */
  getPatientEncountersById(
    patientId: string,
    opts?: { bypassCache?: boolean }
  ): Observable<Appointment[]> {
    const pid = (patientId || "").trim();
    if (!pid) {
      return of([]);
    }

    if (opts?.bypassCache) {
      this.patientEncountersInflight.delete(pid);
    } else {
      const cached = this._applicationCacheService.applicationCache.patientEncounters || [];
      const cachePatientId =
        this.patientEncountersCachePatientId ||
        PatientService.resolveEncountersOwnerPatientId(cached);
      if (
        cached.length > 0 &&
        cachePatientId === pid &&
        PatientService.encountersBelongToPatient(cached, pid)
      ) {
        this.patientEncountersCachePatientId = pid;
        return of(cached.map((row) => ({ ...row })));
      }
      if (cached.length > 0 && cachePatientId !== pid) {
        this.clearPatientEncountersCacheUnlessPatient(pid);
      }
      const inflight = this.patientEncountersInflight.get(pid);
      if (inflight) {
        return inflight;
      }
    }

    const url = this.apiBaseUrl + `api/v1/encounters/patient/${pid}`;
    const req$ = this._dataService.get(url).pipe(
      map((raw) =>
        this.unwrapPatientEncountersPayload(raw).map((e) => this.normalizeEncounterForUi(e))
      ),
      catchError((error) => throwError(() => error)),
      tap((encounters: Appointment[]) => {
        this.setPatientEncountersCache(pid, encounters);
      }),
      finalize(() => {
        this.patientEncountersInflight.delete(pid);
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );
    this.patientEncountersInflight.set(pid, req$);
    return req$;
  }

  /**
   * PATCH `api/v1/encounters/patient/{patientId}` — persists schedule-level patient data (e.g. biometrics on `schedulePatient`).
   */
  patchPatientEncountersByPatientId(
    patientId: string,
    body: Record<string, unknown>
  ): Observable<unknown> {
    const url =
      this.apiBaseUrl + `api/v1/encounters/patient/${encodeURIComponent(patientId)}`;
    return this._dataService.patch(url, body).pipe(
      catchError((error) => {
return throwError(() => error);
      })
    );
  }

  private unwrapPatientEncountersPayload(raw: unknown): Appointment[] {
    if (Array.isArray(raw)) {
      return raw as Appointment[];
    }
    if (raw && typeof raw === "object") {
      const o = raw as Record<string, unknown>;
      if (Array.isArray(o.data)) return o.data as Appointment[];
      if (Array.isArray(o.encounters)) return o.encounters as Appointment[];
      if (Array.isArray(o.items)) return o.items as Appointment[];
      if (Array.isArray(o.result)) return o.result as Appointment[];
      if (Array.isArray(o.Data)) return o.Data as Appointment[];
      if (Array.isArray(o.Encounters)) return o.Encounters as Appointment[];
      if (Array.isArray(o.Items)) return o.Items as Appointment[];
    }
    return [];
  }

  private normalizeEncounterForUi(appt: Appointment): Appointment {
    if (!appt || typeof appt !== "object") {
      return appt;
    }
    const a = { ...appt } as Appointment & Record<string, unknown>;
    this.decryptPhiOnEncounterPatient(a);
    PatientService.flattenEncounterScheduleIfArray(a);
    this.applyPatientEncounterAliases(a);
    this.mergeSchedulePatientAnthroOntoEncounter(a);
    const s = a.encounterSchedule;
    const schedRec = s as unknown as Record<string, unknown> | undefined;
    const hasUsefulSchedule =
      s &&
      (String(s.procedureRoom || "").trim() ||
        String(schedRec?.["procedure_room"] || "").trim() ||
        String(PatientService.coerceLocationString(schedRec?.["location"])).trim() ||
        String((schedRec?.["locationName"] as string) || "").trim() ||
        String((schedRec?.["location_name"] as string) || "").trim() ||
        String(s.scheduleDate || "").trim() ||
        String(s.checkIn || "").trim() ||
        String(s.visitType || "").trim() ||
        String(s.scheduleStatus || "").trim() ||
        String(schedRec?.["schedule_status"] || "").trim());
    if (hasUsefulSchedule) {
      this.mergeProcedureRoomFromRoot(a);
      this.mergeScheduleStatusFromRoot(a);
      return a;
    }

    const ax = a as Record<string, unknown>;
    const schedAny = s as unknown as Record<string, unknown> | undefined;
    const procedureRoom =
      PatientService.coerceTextField(s?.procedureRoom) ||
      PatientService.coerceTextField(schedAny?.["procedure_room"]) ||
      PatientService.coerceTextField(ax["procedureRoom"]) ||
      PatientService.coerceTextField(ax["procedure_room"]) ||
      "";
    const scheduleDate = String(
      (s?.scheduleDate as string) ||
        (a.scheduleDate as string) ||
        (a.encounterDateTime as string) ||
        ""
    );
    const checkIn = String((s?.checkIn as string) || (a.checkIn as string) || "");
    const visitType = String(
      (s?.visitType as string) || (a.visitType as string) || ""
    );
    const scheduleResourceId = String(
      (s?.scheduleResourceId as string) ||
        (a.scheduleResourceId as string) ||
        (a.resourceId as string) ||
        ""
    );
    const scheduleStatus =
      PatientService.coerceTextField(a.scheduleStatus) ||
      PatientService.coerceTextField(ax["schedule_status"]) ||
      PatientService.coerceTextField(ax["scheduleStatus"]) ||
      PatientService.coerceTextField(s?.scheduleStatus) ||
      PatientService.coerceTextField(schedAny?.["schedule_status"]) ||
      "";

    if (
      !procedureRoom &&
      !scheduleDate &&
      !checkIn &&
      !visitType &&
      !scheduleResourceId &&
      !scheduleStatus
    ) {
      this.mergeProcedureRoomFromRoot(a);
      this.mergeScheduleStatusFromRoot(a);
      return a;
    }

    a.encounterSchedule = {
      ...(s || {}),
      procedureRoom: procedureRoom || s?.procedureRoom || "",
      scheduleDate: scheduleDate || s?.scheduleDate || "",
      checkIn: checkIn || s?.checkIn || "",
      visitType: visitType || s?.visitType || "",
      scheduleResourceId: scheduleResourceId || s?.scheduleResourceId || "",
      scheduleStatus: scheduleStatus || s?.scheduleStatus || "",
    } as Appointment["encounterSchedule"];
    if (scheduleStatus) {
      a.scheduleStatus = scheduleStatus;
    }
    this.mergeProcedureRoomFromRoot(a);
    this.mergeScheduleStatusFromRoot(a);
    return a;
  }

  /** If schedule exists but room is blank, copy `procedureRoom` from root (`GET .../encounters/patient/...`). */
  private mergeProcedureRoomFromRoot(a: Appointment): void {
    const s = a.encounterSchedule;
    if (!s) return;
    if (PatientService.coerceTextField(s.procedureRoom)) return;
    const ext = a as Appointment & Record<string, unknown>;
    const sched = s as unknown as Record<string, unknown>;
    const pr =
      PatientService.coerceTextField(ext.procedureRoom) ||
      PatientService.coerceTextField(ext.procedure_room) ||
      PatientService.pickProcedureRoomFromRecord(ext) ||
      PatientService.coerceTextField(sched["procedure_room"]) ||
      PatientService.coerceTextField(sched["procedureRoom"]) ||
      PatientService.pickProcedureRoomFromRecord(sched);
    if (pr) {
      a.encounterSchedule = { ...s, procedureRoom: pr } as Appointment["encounterSchedule"];
    }
  }

  /** Align `scheduleStatus` on root and nested schedule (patient-encounters may use either). */
  private mergeScheduleStatusFromRoot(a: Appointment): void {
    const ext = a as Appointment & Record<string, unknown>;
    const s = a.encounterSchedule;
    const sched = s as unknown as Record<string, unknown> | undefined;
    const fromRoot =
      PatientService.coerceTextField(a.scheduleStatus) ||
      PatientService.coerceTextField(ext["schedule_status"]) ||
      PatientService.coerceTextField(ext["scheduleStatus"]) ||
      PatientService.pickScheduleStatusFromRecord(ext);
    const fromSchedule = s
      ? PatientService.coerceTextField(s.scheduleStatus) ||
        PatientService.coerceTextField(sched?.["schedule_status"]) ||
        PatientService.pickScheduleStatusFromRecord(s as unknown as Record<string, unknown>)
      : "";
    const canonical = fromRoot || fromSchedule;
    if (!canonical) {
      return;
    }
    if (!PatientService.coerceTextField(a.scheduleStatus)) {
      a.scheduleStatus = canonical;
    }
    if (s && !PatientService.coerceTextField(s.scheduleStatus)) {
      a.encounterSchedule = { ...s, scheduleStatus: canonical } as Appointment["encounterSchedule"];
    }
  }

  insertPatient(
    patient: Partial<Patient> | PhiPatientApiRecord,
    options?: PhiPatientWriteOptions
  ): Observable<PhiPatientApiResponse> {
    const url = this.apiBaseUrl + "api/v1/patients";
    return this._dataService.post(url, patient).pipe(
      switchMap((res: unknown) =>
        this.finalizePhiPatientWriteResponse(res, patient, undefined, options)
      )
    );
  }

  updatPatient(
    patientId: string,
    patient: UpsertPatient | PhiPatientApiRecord,
    options?: PhiPatientWriteOptions
  ): Observable<PhiPatientApiResponse> {
    const url = this.apiBaseUrl + `api/v1/patients/${patientId}`;
    return this._dataService.patch(url, patient).pipe(
      switchMap((res: unknown) =>
        this.finalizePhiPatientWriteResponse(res, patient, patientId, options)
      ),
      tap((merged) => {
        const id = (patientId || merged?.id || "").trim();
        if (id) {
          this._applicationCacheService.updatePatientInfoCache({
            ...(this._applicationCacheService.applicationCache.patient || {}),
            ...merged,
            id,
          } as Patient);
        }
      })
    );
  }

  /** `PATCH api/v1/patients/{id}` — height, weight, measuredOn (patient details Bio Metrics). */
  patchPatientBiometrics(
    patientId: string,
    biometrics: PatientBiometricsPatch
  ): Observable<PhiPatientApiResponse> {
    return this.updatPatient(patientId, biometrics);
  }

  /**
   * POST/PATCH body may be empty in DevTools while create still succeeds. Coalesce the request,
   * then GET by id when the write response only returned an id (or nothing).
   */
  private finalizePhiPatientWriteResponse(
    res: unknown,
    request: Partial<Patient> | PhiPatientApiRecord | UpsertPatient,
    knownPatientId?: string,
    options?: PhiPatientWriteOptions
  ): Observable<PhiPatientApiResponse> {
    let merged = coalescePhiPatientPostResponse(res, request);
    const id = (knownPatientId || merged.id || "").trim();
    if (id) {
      merged = { ...merged, id };
    }
    if (options?.skipGetByIdHydrate) {
      return of(merged);
    }
    const req = request as PhiPatientApiRecord;
    const reqReferralSourceId =
      normalizeEngageReferralSourceIdField(req.referralSource) ||
      (req.referral_source || "").trim();
    const mergedReferralSourceId =
      normalizeEngageReferralSourceIdField(merged.referralSource) ||
      (merged.referral_source || "").trim();
    const requestHasPharmacy = PATIENT_PHARMACY_FIELD_KEYS.some(
      (key) => String(req[key] ?? "").trim() !== ""
    );
    const mergedHasPharmacy = PATIENT_PHARMACY_FIELD_KEYS.some(
      (key) => String(merged[key] ?? "").trim() !== ""
    );
    const needsPharmacyHydrate = requestHasPharmacy && !mergedHasPharmacy;

    const needsHydrate =
      !!id &&
      (!(merged.firstName || "").trim() ||
        !(merged.referralId || "").trim() ||
        !(merged.leadStatus || "").trim() ||
        (!!reqReferralSourceId && !mergedReferralSourceId) ||
        (!!(req.service || "").trim() && !(merged.service || "").trim()) ||
        needsPharmacyHydrate);
    if (!needsHydrate) {
      return of(merged);
    }
    return this.getPatientById(id, { force: true }).pipe(
      map((full) => coalescePhiPatientPostResponse(full, merged)),
      catchError(() => of(merged))
    );
  }

  deletePatient(patientId: string): Observable<void> {
    const url = this.apiBaseUrl + `api/v1/patients/${patientId}`;
    return this._dataService.delete(url);
  }

  get apiBaseUrl(): string {
    return environment.apiBaseUrl;
  }

  getPatientImage(patientImage: PatientImage) {
    if (!patientImage) {
      return "/assets/images/custom/patient-avatar.png";
    }
    return `data:${patientImage.contentType};base64, ${patientImage.base64String}`;
  }

  searchPatientByPartialName(
    searchTerm: string
  ): Observable<PatientSearchResult[]> {
    if (!searchTerm) {
      return of(null);
    }
    const url = `${
      this.apiBaseUrl
    }api/v1/patients/search/term?term=${encodeURIComponent(searchTerm)}`;
    console.log(url)
    return this._dataService.get(url);
  }

  /** PHI phone duplicate check — `{ exists, patientIds }` from `GET …/search/phone/term`. */
  searchPatientByPhone(term: string): Observable<unknown> {
    const digits = String(term ?? "").replace(/\D/g, "");
    const tenDigits =
      digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
    if (tenDigits.length !== 10) {
      return of({ exists: false, patientIds: [] });
    }
    const url = `${
      this.apiBaseUrl
    }api/v1/patients/search/phone/term?term=${encodeURIComponent(tenDigits)}`;
    return this._dataService
      .get(url)
      .pipe(catchError(() => of({ exists: false, patientIds: [] })));
  }

  private get isPatientAlreadyLoaded(): boolean {
    return (
      this._applicationCacheService.getApiStatus(ReferralApis.Patient) ===
      ApiStatus.Success
    );
  }

  get patientCache(): Patient {
    return cloneDeep(this._applicationCacheService.applicationCache.patient);
  }

  getPatientContacts(patientId: string): Observable<PatientContact[]> {
    const url = this.apiBaseUrl + `api/v1/patient-contacts/patient/${patientId}`;
    return this._dataService.get(url).pipe(
      catchError((error) => {
return throwError(() => error);
      })
    );
  }

  savePatientContact(contact: PatientContact): Observable<PatientContact> {
    const url = this.apiBaseUrl + `api/v1/patient-contacts`;
    return this._dataService.post(url, contact).pipe(
      catchError((error) => {
return throwError(() => error);
      })
    );
  }

  updatePatientContact(contactId: string, contact: PatientContact): Observable<PatientContact> {
    const url = this.apiBaseUrl + `api/v1/patient-contacts/${contactId}`;
    return this._dataService.patch(url, contact).pipe(
      catchError((error) => {
return throwError(() => error);
      })
    );
  }

  deletePatientContact(contactId: string): Observable<void> {
    const url = this.apiBaseUrl + `api/v1/patient-contacts/${contactId}`;
    return this._dataService.delete(url).pipe(
      catchError((error) => {
return throwError(() => error);
      })
    );
  }
}
